import React, { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";

const EMPTY = { name: "", description: "", cfu_partial: "", hours: "", order: 0, teacher_ids: [] };

export default function ModuleFormDialog({ open, onOpenChange, mode = "create", initial, onSubmit, submitting }) {
  const [form, setForm] = useState(EMPTY);
  const { data: teachers = [] } = useQuery({
    queryKey: ["teachers-module"],
    queryFn: async () => (await api.get("/teachers")).data,
    enabled: open,
  });

  useEffect(() => {
    if (open) setForm(initial ? { ...EMPTY, ...initial, teacher_ids: initial.teacher_ids || [] } : EMPTY);
  }, [open, initial]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const toggle = (id) =>
    setForm((p) => ({ ...p, teacher_ids: p.teacher_ids.includes(id) ? p.teacher_ids.filter((x) => x !== id) : [...p.teacher_ids, id] }));

  function submit(e) {
    e.preventDefault();
    const payload = { ...form };
    payload.cfu_partial = payload.cfu_partial === "" ? null : parseFloat(payload.cfu_partial);
    payload.hours = payload.hours === "" ? null : parseInt(payload.hours);
    payload.description = payload.description || null;
    onSubmit(payload);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg bg-white">
        <DialogHeader>
          <DialogTitle>{mode === "create" ? "Nuovo modulo" : "Modifica modulo"}</DialogTitle>
          <DialogDescription>Sotto-parte di un corso, con propri docenti (es. La Persona di Dio all&apos;interno di Sistematica 1).</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="text-xs">Nome modulo</Label>
            <Input data-testid="module-name" value={form.name} onChange={(e) => set("name", e.target.value)} required placeholder="La Persona di Dio" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">CFU parziale</Label>
              <Input type="number" step="0.5" value={form.cfu_partial} onChange={(e) => set("cfu_partial", e.target.value)} />
            </div>
            <div>
              <Label className="text-xs">Ore</Label>
              <Input type="number" value={form.hours} onChange={(e) => set("hours", e.target.value)} />
            </div>
            <div>
              <Label className="text-xs">Ordine</Label>
              <Input type="number" value={form.order ?? 0} onChange={(e) => set("order", parseInt(e.target.value) || 0)} />
            </div>
          </div>
          <div>
            <Label className="text-xs">Descrizione</Label>
            <Textarea rows={2} value={form.description || ""} onChange={(e) => set("description", e.target.value)} />
          </div>
          <div>
            <Label className="text-xs">Docenti del modulo</Label>
            <div className="mt-1.5 flex flex-wrap gap-2 max-h-40 overflow-y-auto p-2 border border-stone-200 rounded-md bg-stone-50/50">
              {teachers.length === 0 ? (
                <span className="text-xs text-stone-500">Nessun docente disponibile.</span>
              ) : teachers.map((t) => {
                const active = form.teacher_ids.includes(t.id);
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => toggle(t.id)}
                    data-testid={`module-teacher-${t.id}`}
                    className={`px-3 py-1 rounded-full text-xs border transition-colors duration-150 ${active ? "bg-[#1C352D] text-white border-[#1C352D]" : "bg-white text-stone-700 border-stone-200 hover:border-[#1C352D]"}`}
                  >
                    {t.first_name} {t.last_name}
                  </button>
                );
              })}
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
            <Button type="submit" disabled={submitting} data-testid="module-submit" className="bg-[#1C352D] hover:bg-[#264A3E]">
              {submitting ? "Salvataggio…" : mode === "create" ? "Crea modulo" : "Salva"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
